import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Users, LogIn, LogOut, Settings, Lock, Send, CheckCircle, Tag, Building2, BarChart3, Sliders, Globe, Wallet, Megaphone, Mail, BookOpen, Gift, Share2 } from 'lucide-react';
import VoucherPrompt from '@/components/vouchers/VoucherPrompt';
import { Button } from '@/components/ui/button';

import ApprovedBadge from '@/components/ApprovedBadge';
import ActivityBanner from '@/components/approvalActivity/ActivityBanner';
import MarketingOnboardingBanner from '@/components/marketing/MarketingOnboardingBanner';
import NotificationBadge from '@/components/NotificationBadge';
import ProPortalHeader from '@/components/ProPortalHeader';
import ImpersonateInstallerCard from '@/components/ImpersonateInstallerCard';
import OnboardingTour from '@/components/onboarding/OnboardingTour';
import useNotificationCounts from '@/hooks/useNotificationCounts';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';
import logger from '@/utils/logger';

// Brand colors for each service
const BRANDS = {
  probound: {
    name: 'ProBound',
    fullName: 'Ox Resin ProBound',
    tagline: 'Resin Bound Surfacing',
    description: 'Driveways, paths & patios with premium resin bound stone',
    primaryColor: '#2F5E96',
    secondaryColor: '#300122',
    logo: 'https://customer-assets.emergentagent.com/job_resin-quote-calc/artifacts/0yxgnasw_IMG_8077.jpeg',
    path: '/probound'
  },
  proepoxy: {
    name: 'ProEpoxy',
    fullName: 'Ox Resin ProEpoxy',
    tagline: 'Epoxy Flooring Systems',
    description: 'Industrial & decorative epoxy floor coatings',
    primaryColor: '#7A0B7A',
    secondaryColor: '#300033',
    logo: 'https://customer-assets.emergentagent.com/job_76625416-d43e-411a-b4d0-3b73c58f517c/artifacts/ta6bz3y6_IMG_8292.jpeg',
    path: '/proepoxy'
  },
  prodecor: {
    name: 'ProDecor',
    fullName: 'Ox Resin ProDecor',
    tagline: 'Decorative Walling',
    description: 'Premium decorative wall finishes & coatings',
    primaryColor: '#D5AB09',
    secondaryColor: '#2B2101',
    logo: 'https://customer-assets.emergentagent.com/job_d9177fe4-220c-4669-9998-86c6e454a508/artifacts/4z707ot7_IMG_8289.jpeg',
    path: '/prodecor'
  },
  protraining: {
    name: 'ProTraining',
    fullName: 'Ox Resin ProTraining',
    tagline: 'Training Courses',
    description: 'Professional installer training & certification',
    primaryColor: '#D35500',
    secondaryColor: '#381E0C',
    logo: 'https://customer-assets.emergentagent.com/job_d9177fe4-220c-4669-9998-86c6e454a508/artifacts/vm7nme87_IMG_8293.jpeg',
    path: '/protraining'
  }
};

const Home = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmbedMode = searchParams.get('embed') === 'true';
  const { user, isAuthenticated, isAdmin, isStaff, hasAccess, logout, loading } = useAuth();
  const [websiteUnread, setWebsiteUnread] = useState(0);
  const [wallet, setWallet] = useState(null);
  const [vouchers, setVouchers] = useState(null);
  useEffect(() => {
    if (!user) return;
    fetch(`${API_URL}/api/website/me`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then((d) => d && setWebsiteUnread((d.orders || []).reduce((a, o) => a + (o.unread || 0), 0))).catch(() => {});
    fetch(`${API_URL}/api/lead-wallet`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then((d) => d && setWallet(d)).catch(() => {});
    if (user.role === 'installer') fetch(`${API_URL}/api/vouchers`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then((d) => d?.claim && setVouchers(d)).catch(() => {});
  }, [user]);
  const [requestingAccess, setRequestingAccess] = useState({});
  const [requestedAccess, setRequestedAccess] = useState({});
  const { bySection: notifBySection } = useNotificationCounts();

  // Load existing pending access requests on mount
  useEffect(() => {
    if (isAuthenticated && user?.role === 'installer') {
      fetch(`${API_URL}/api/access-requests/my-pending`, {
        credentials: 'include'
      })
        .then(res => res.ok ? res.json() : [])
        .then(data => {
          const pending = {};
          data.forEach(req => { pending[req.section] = true; });
          setRequestedAccess(pending);
        })
        .catch(() => {});
    }
  }, [isAuthenticated, user?.role]);

  const handleNavigate = (path) => {
    if (isEmbedMode) {
      navigate(`${path}?embed=true`);
    } else {
      navigate(path);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleRequestAccess = async (section) => {
    setRequestingAccess(prev => ({ ...prev, [section]: true }));
    try {
      const response = await fetch(`${API_URL}/api/access-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          section, 
          message: `Requesting access to ${section.toUpperCase()} tools` 
        })
      });
      
      if (response.ok) {
        setRequestedAccess(prev => ({ ...prev, [section]: true }));
      } else {
        const text = await response.text();
        let detail = 'Failed to request access';
        try { detail = formatApiError(JSON.parse(text)) || detail; } catch { /* keep default */ }
        if (detail.includes('already have')) {
          setRequestedAccess(prev => ({ ...prev, [section]: true }));
        } else {
          alert(detail);
        }
      }
    } catch (error) {
      logger.error('Error requesting access:', error);
      alert('Failed to request access. Please try again.');
    } finally {
      setRequestingAccess(prev => ({ ...prev, [section]: false }));
    }
  };

  // Get role badge color
  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-red-500',
      manager: 'bg-purple-500',
      team: 'bg-blue-500',
      installer: 'bg-green-500'
    };
    return colors[role] || 'bg-gray-500';
  };

  // If not authenticated, show login prompt
  if (!loading && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#E8ECEF] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-[#27292E] rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#27292E] mb-2">Ox Resin Pro Installer Portal</h1>
          <p className="text-gray-500 mb-6">Please log in to access the installer tools and resources.</p>
          <Button
            onClick={() => navigate('/login')}
            className="w-full bg-[#27292E] hover:bg-[#3a3d45] text-white py-3 mb-3"
          >
            <LogIn className="w-5 h-5 mr-2" />
            Log In
          </Button>
          <Button
            onClick={() => navigate('/login?register=true')}
            variant="outline"
            className="w-full border-[#27292E] text-[#27292E] hover:bg-gray-50 py-3"
          >
            Create an Account
          </Button>
          <p className="text-xs text-gray-400 mt-4">
            New accounts require access to be granted by Ox Resin staff.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      {/* First-login walkthrough — only fires for bulk-invited installers
          who haven't dismissed it yet. Self-managed; no props needed. */}
      <OnboardingTour />
      <VoucherPrompt />

      {/* Header - Hidden in embed mode */}
      {!isEmbedMode && <ProPortalHeader activeSection="home" />}

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#27292E] mb-2">
            Welcome to the Installer Portal
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Select a service to access quotes, orders, and training materials.
          </p>

          {/* Staff-only: installer account lookup */}
          {user && ['admin', 'manager', 'team'].includes(user.role) && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const code = (e.target.elements['installer-code'].value || '').trim();
                if (code) navigate(`/staff/installer/${encodeURIComponent(code.toUpperCase())}`);
              }}
              className="mt-6 max-w-md mx-auto flex gap-2"
              data-testid="staff-installer-lookup"
            >
              <input
                name="installer-code"
                placeholder="Look up installer (account code, email or phone)"
                className="flex-1 border-2 border-gray-200 focus:border-[#006400] outline-none rounded-lg px-4 py-2 text-sm font-mono uppercase"
                autoComplete="off"
              />
              <button
                type="submit"
                className="bg-[#006400] hover:bg-[#004d00] text-white font-semibold rounded-lg px-4 py-2 text-sm"
                data-testid="staff-installer-lookup-submit"
              >
                Look up
              </button>
            </form>
          )}

          {/* Staff-only: act as installer (impersonation) */}
          <ImpersonateInstallerCard />

          {isAuthenticated && !isStaff && <div className="max-w-md mx-auto"><MarketingOnboardingBanner /><ActivityBanner /></div>}

          {/* Ambassador Dashboard shortcut — visible only to users who
              have been promoted to Ambassador. Silent to non-promoted
              installers so no one can guess the flow exists. */}
          {isAuthenticated && user?.affiliate_own_id && (
            <button
              onClick={() => navigate('/affiliate')}
              className="mt-6 max-w-md mx-auto flex items-center justify-between gap-3 rounded-xl px-4 py-3 border-2 border-[#7A0B7A]/30 bg-[#7A0B7A]/5 hover:bg-[#7A0B7A]/10 text-left transition"
              data-testid="ambassador-dashboard-link"
            >
              <div>
                <p className="text-xs uppercase tracking-wider text-[#7A0B7A] font-bold">Ox Ambassador</p>
                <p className="text-sm text-gray-700 mt-0.5">See your share link, referred customers &amp; commission earned</p>
              </div>
              <span className="text-[#7A0B7A] font-bold">→</span>
            </button>
          )}
        </div>

        {/* Service Cards - 2x2 Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
          {Object.entries(BRANDS).map(([key, brand]) => {
            const hasServiceAccess = hasAccess(key);
            const isLocked = !hasServiceAccess;
            
            return (
              <div
                key={key}
                onClick={() => hasServiceAccess ? handleNavigate(brand.path) : null}
                role={hasServiceAccess ? "button" : undefined}
                tabIndex={hasServiceAccess ? 0 : undefined}
                className={`group relative overflow-hidden rounded-2xl p-6 md:p-8 text-left transition-all duration-300 ${
                  isLocked 
                    ? 'cursor-default' 
                    : 'cursor-pointer transform hover:scale-[1.02] hover:shadow-xl'
                }`}
                style={{ backgroundColor: isLocked ? '#9CA3AF' : brand.primaryColor }}
                data-testid={`nav-${key}`}
              >
                {/* Unread notifications badge */}
                {!isLocked && <NotificationBadge count={notifBySection[key]} position="top-right" size="md" />}

                {/* Background gradient overlay */}
                <div 
                  className="absolute inset-0 opacity-50"
                  style={{ 
                    background: isLocked 
                      ? 'linear-gradient(135deg, transparent 0%, #6B7280 100%)'
                      : `linear-gradient(135deg, transparent 0%, ${brand.secondaryColor} 100%)` 
                  }}
                />
                
                {/* Locked Overlay */}
                {isLocked && (
                  <div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center z-20 gap-3">
                    <div className="bg-white/90 rounded-xl px-4 py-2 flex items-center gap-2 shadow-lg">
                      <Lock className="w-5 h-5 text-gray-700" />
                      <span className="font-bold text-gray-700">Access Required</span>
                    </div>
                    {['probound', 'proepoxy', 'prodecor'].includes(key) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleNavigate(`/notes/${key}`); }}
                      className="bg-amber-400 hover:bg-amber-300 rounded-xl px-4 py-2 flex items-center gap-2 shadow-lg transition-colors"
                      data-testid={`notes-open-${key}`}
                    >
                      <BookOpen className="w-4 h-4 text-[#27292E]" />
                      <span className="text-sm font-bold text-[#27292E]">Training notes — open to all</span>
                    </button>
                    )}
                    {requestedAccess[key] ? (
                      <div className="bg-green-100 rounded-xl px-4 py-2 flex items-center gap-2 shadow-lg">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700">Request Submitted</span>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRequestAccess(key); }}
                        disabled={requestingAccess[key]}
                        className="bg-white hover:bg-gray-50 rounded-xl px-4 py-2 flex items-center gap-2 shadow-lg transition-colors disabled:opacity-50"
                      >
                        <Send className="w-4 h-4 text-[#300122]" />
                        <span className="text-sm font-medium text-[#300122]">
                          {requestingAccess[key] ? 'Requesting...' : 'Request Access'}
                        </span>
                      </button>
                    )}
                  </div>
                )}
                
                <div className="relative z-10">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl md:text-2xl font-bold text-white mb-1">
                        {brand.name}
                      </h3>
                      <p className="text-white/90 text-sm font-medium">
                        {brand.tagline}
                      </p>
                    </div>
                    <img 
                      src={brand.logo} 
                      alt={brand.fullName}
                      className={`h-12 md:h-14 object-contain bg-white rounded-lg p-1 ${isLocked ? 'opacity-50' : ''}`}
                    />
                  </div>
                  <p className="text-white/80 text-sm">
                    {brand.description}
                  </p>
                  <div className={`mt-4 flex items-center ${isLocked ? 'text-white/40' : 'text-white/60 group-hover:text-white'} transition-colors`}>
                    <span className="text-sm font-medium">{isLocked ? 'Contact Ox Resin for access' : 'Enter Portal'}</span>
                    {!isLocked && (
                      <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Leads & Projects now accessible within each section above */}

        {/* Get Support — available to everyone signed in (installers + staff) */}
        {user && (
          <div className="max-w-4xl mx-auto mt-6">
            <button
              onClick={() => navigate('/support')}
              data-testid="home-support-button"
              className="w-full bg-white hover:bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 flex items-center justify-between gap-3 shadow-sm transition group"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="4" />
                    <line x1="4.93" y1="4.93" x2="9.17" y2="9.17" />
                    <line x1="14.83" y1="14.83" x2="19.07" y2="19.07" />
                    <line x1="14.83" y1="9.17" x2="19.07" y2="4.93" />
                    <line x1="14.83" y1="9.17" x2="18.36" y2="5.64" />
                    <line x1="4.93" y1="19.07" x2="9.17" y2="14.83" />
                  </svg>
                </div>
                <div className="min-w-0 text-left">
                  <div className="font-bold text-slate-900 text-sm md:text-base">Need help? Request support</div>
                  <div className="text-slate-500 text-xs">Ask a question, report a problem or send photos — we'll come back to you.</div>
                </div>
              </div>
              <svg className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              onClick={() => navigate('/boards')}
              data-testid="home-boards-button"
              className="w-full bg-white hover:bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 flex items-center justify-between gap-3 shadow-sm transition group mt-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-fuchsia-600 text-white flex items-center justify-center shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 2 7 12 12 22 7 12 2" />
                    <polyline points="2 17 12 22 22 17" />
                    <polyline points="2 12 12 17 22 12" />
                  </svg>
                </div>
                <div className="min-w-0 text-left">
                  <div className="font-bold text-slate-900 text-sm md:text-base">My Boards</div>
                  <div className="text-slate-500 text-xs">Build your own Kanban with stages that suit your workflow.</div>
                </div>
              </div>
              <svg className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              onClick={() => navigate('/help')}
              data-testid="home-guide-button"
              className="w-full bg-white hover:bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 flex items-center justify-between gap-3 shadow-sm transition group mt-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-[#2F5E96] text-white flex items-center justify-center shrink-0">
                  <BookOpen className="w-5 h-5" />
                </div>
                <div className="min-w-0 text-left">
                  <div className="font-bold text-slate-900 text-sm md:text-base">How to use the portal</div>
                  <div className="text-slate-500 text-xs">Short guide to quotes, orders, leads, Ox Approved, your website and more.</div>
                </div>
              </div>
              <svg className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              onClick={() => navigate('/suggest')}
              data-testid="home-suggest-button"
              className="w-full bg-white hover:bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 flex items-center justify-between gap-3 shadow-sm transition group mt-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18h6" /><path d="M10 22h4" />
                    <path d="M15 14a5 5 0 1 0-6 0c1 1 1 2 1 3h4c0-1 0-2 1-3Z" />
                  </svg>
                </div>
                <div className="min-w-0 text-left">
                  <div className="font-bold text-slate-900 text-sm md:text-base">Suggest an Improvement</div>
                  <div className="text-slate-500 text-xs">Got an idea to make the portal better? We'd love to hear it.</div>
                </div>
              </div>
              <svg className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              onClick={() => navigate('/marketing')}
              data-testid="home-marketing-button"
              className="w-full bg-white hover:bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 flex items-center justify-between gap-3 shadow-sm transition group mt-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-fuchsia-700 text-white flex items-center justify-center shrink-0"><Megaphone className="w-5 h-5" /></div>
                <div className="min-w-0 text-left">
                  <div className="font-bold text-slate-900 text-sm md:text-base">Marketing Hub</div>
                  <div className="text-slate-500 text-xs">Turn your job photos into ready-to-post social content — captions written for you, plus text ideas for past customers.</div>
                </div>
              </div>
              <svg className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
            <button
              onClick={() => navigate('/marketing/social')}
              data-testid="home-social-button"
              className="w-full bg-white hover:bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 flex items-center justify-between gap-3 shadow-sm transition group mt-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-[#1877F2] text-white flex items-center justify-center shrink-0"><Share2 className="w-5 h-5" /></div>
                <div className="min-w-0 text-left">
                  <div className="font-bold text-slate-900 text-sm md:text-base">Social Posting</div>
                  <div className="text-slate-500 text-xs">Post to your Facebook page and Instagram from the app — now or scheduled — and use Ox Resin's ready-made posts.</div>
                </div>
              </div>
              <svg className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
<button
              onClick={() => navigate('/installer/email-templates')}
              data-testid="home-email-templates-button"
              className="w-full bg-white hover:bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 flex items-center justify-between gap-3 shadow-sm transition group mt-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-violet-700 text-white flex items-center justify-center shrink-0"><Mail className="w-5 h-5" /></div>
                <div className="min-w-0 text-left">
                  <div className="font-bold text-slate-900 text-sm md:text-base">Customer email templates</div>
                  <div className="text-slate-500 text-xs">Ready-written emails for quotes, follow-ups and aftercare — add the customer's name and send from your portal.</div>
                </div>
              </div>
              <svg className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
            <button
              onClick={() => navigate('/lead-wallet')}
              data-testid="home-lead-wallet-button"
              className="w-full bg-white hover:bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 flex items-center justify-between gap-3 shadow-sm transition group mt-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-emerald-700 text-white flex items-center justify-center shrink-0"><Wallet className="w-5 h-5" /></div>
                <div className="min-w-0 text-left">
                  <div className="font-bold text-slate-900 text-sm md:text-base">Lead Wallet{wallet && <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-full ${wallet.low ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`} data-testid="home-lead-credit">£{Number(wallet.balance).toFixed(2)}</span>}</div>
                  <div className="text-slate-500 text-xs">{wallet?.enabled ? `Prepaid lead credit — £${Number(wallet.price_per_lead).toFixed(0)} per accepted lead. Top up so you never miss one.` : 'Your prepaid lead credit and history.'}</div>
                </div>
              </div>
              <svg className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
            {vouchers && (
              <button
                onClick={() => navigate('/vouchers')}
                data-testid="home-vouchers-button"
                className="w-full bg-white hover:bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 flex items-center justify-between gap-3 shadow-sm transition group mt-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-amber-500 text-[#27292E] flex items-center justify-center shrink-0"><Gift className="w-5 h-5" /></div>
                  <div className="min-w-0 text-left">
                    <div className="font-bold text-slate-900 text-sm md:text-base">My Vouchers{vouchers.prompt && <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="home-vouchers-unclaimed">£{Number(vouchers.totals?.locked || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })} unclaimed</span>}</div>
                    <div className="text-slate-500 text-xs">{vouchers.prompt ? 'Leave us a Google review to unlock money off your products.' : 'Your training vouchers — use them on your orders.'}</div>
                  </div>
                </div>
                <svg className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            )}
            <button
              onClick={() => navigate('/my-website')}
              data-testid="home-website-button"
              className="w-full bg-white hover:bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 flex items-center justify-between gap-3 shadow-sm transition group mt-3 relative"
            >
              {websiteUnread > 0 && <NotificationBadge count={websiteUnread} position="top-right" size="md" />}
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-teal-700 text-white flex items-center justify-center shrink-0">
                  <Globe className="w-5 h-5" />
                </div>
                <div className="min-w-0 text-left">
                  <div className="font-bold text-slate-900 text-sm md:text-base">Get Your Website</div>
                  <div className="text-slate-500 text-xs">Pick a design, tell us about your business and we'll build, host and look after it — £200 setup, £50 a month.</div>
                </div>
              </div>
              <svg className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </main>

      {/* Footer - Hidden in embed mode */}
      {!isEmbedMode && (
        <footer className="mt-auto py-6 text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Ox Resin. All rights reserved.</p>
        </footer>
      )}
    </div>
  );
};

export default Home;
